import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  CalendarCheck, Loader2, Plus, Lock, Unlock, CheckCircle2, XCircle,
  AlertTriangle, Calendar, Check, X,
} from 'lucide-react';
import { RestrictedHolidayListView, RestrictedHolidayOptionView } from '../../../models/attendance';
import { HolidayView } from '../../../models/general';
import * as LeavesApi from '../../../api/Attendance/leaves';
import * as HolidaysApi from '../../../api/General/holidays';
import Button from '../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '../../../components/ui/Dialog';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { appDate } from '../../../utils/appDateFormat';
import { DateField } from '../../../components/dateFields';

interface RestrictedHolidaysProps {
  userId: string;
  company: { id: number };
  role: string;
  embedded?: boolean;
}

const statusConfig: Record<string, { bgClass: string; icon: React.ElementType }> = {
  Draft: { bgClass: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
  Open: { bgClass: 'bg-green-100 text-green-800', icon: Unlock },
  Closed: { bgClass: 'bg-muted text-muted-foreground', icon: Lock },
};

function formatDate(dateStr: string): string {
  return appDate(new Date(dateStr));
}

const RestrictedHolidays: React.FC<RestrictedHolidaysProps> = ({ company, role, embedded }) => {
  const { t } = useTranslation(['leaves', 'common']);
  // HR-ONLY on purpose (not Hr+Admin+Chief): create/open/close and the per-option
  // "N selected" tally are gated server-side by RestrictedHolidayService's
  // IsInRoleAsync("Hr"), so anyone else was shown buttons the API rejects. No change
  // for the embedded Leave-Settings tab (that route is already HR_ONLY); it matters
  // now that the page is also mounted standalone at /restricted-holidays for every
  // employee, which is where the RestrictedHoliday pending action lands.
  const isHr = role === 'Hr';

  const [lists, setLists] = useState<RestrictedHolidayListView[]>([]);
  const [holidays, setHolidays] = useState<HolidayView[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [createModal, setCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const [form, setForm] = useState({
    name: '',
    year: new Date().getFullYear(),
    maxSelections: 3,
    selectionStartDate: '',
    selectionEndDate: '',
    selectedHolidayIds: [] as number[],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await LeavesApi.getRestrictedHolidayLists();
      if (res.errors.length === 0) setLists(res.payload);
    } catch {
      setToast({ message: t('restrictedLoadError'), type: 'error' });
    }
    setLoading(false);
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const loadHolidays = async () => {
    try {
      const res = await HolidaysApi.getHolidaysConfig(company.id);
      if (res.errors.length === 0) {
        const items = res.payload.holidayConfigInputList
          .filter((h: any) => h.enabled)
          .map((h: any) => ({
            id: h.id,
            name: h.holiday?.name ?? '',
            description: '',
            date: h.date,
          }));
        setHolidays(items);
      }
    } catch { /* ignore */ }
  };

  const handleCreate = async () => {
    if (!form.name || !form.selectionStartDate || !form.selectionEndDate || form.selectedHolidayIds.length === 0) return;
    setCreateLoading(true);
    try {
      const result = await LeavesApi.createRestrictedHolidayList({
        name: form.name,
        year: form.year,
        maxSelections: form.maxSelections,
        selectionStartDate: form.selectionStartDate,
        selectionEndDate: form.selectionEndDate,
        holidayIds: form.selectedHolidayIds,
      });
      if (result.errors.length === 0) {
        setToast({ message: t('restrictedListCreated'), type: 'success' });
        setCreateModal(false);
        setForm({ name: '', year: new Date().getFullYear(), maxSelections: 3, selectionStartDate: '', selectionEndDate: '', selectedHolidayIds: [] });
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('restrictedCreateError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('restrictedCreateError')), type: 'error' });
    }
    setCreateLoading(false);
  };

  const handleOpenList = async (listId: number) => {
    setActionLoading(`open-${listId}`);
    try {
      const result = await LeavesApi.openRestrictedHolidayList(listId);
      if (result.errors.length === 0) {
        setToast({ message: t('restrictedListOpened'), type: 'success' });
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('restrictedOpenError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('restrictedOpenError')), type: 'error' });
    }
    setActionLoading(null);
  };

  const handleCloseList = async (listId: number) => {
    setActionLoading(`close-${listId}`);
    try {
      const result = await LeavesApi.closeRestrictedHolidayList(listId);
      if (result.errors.length === 0) {
        setToast({ message: t('restrictedListClosed'), type: 'success' });
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('restrictedCloseError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('restrictedCloseError')), type: 'error' });
    }
    setActionLoading(null);
  };

  const handleSelect = async (listId: number, optionId: number) => {
    setActionLoading(`select-${optionId}`);
    try {
      const result = await LeavesApi.selectRestrictedHoliday(listId, { optionId });
      if (result.errors.length === 0) {
        setToast({ message: t('restrictedHolidaySelected'), type: 'success' });
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('restrictedSelectError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('restrictedSelectError')), type: 'error' });
    }
    setActionLoading(null);
  };

  const handleDeselect = async (listId: number, optionId: number) => {
    setActionLoading(`deselect-${optionId}`);
    try {
      const result = await LeavesApi.deselectRestrictedHoliday(listId, optionId);
      if (result.errors.length === 0) {
        setToast({ message: t('restrictedHolidayDeselected'), type: 'success' });
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('restrictedDeselectError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('restrictedDeselectError')), type: 'error' });
    }
    setActionLoading(null);
  };

  const toggleHolidayId = (id: number) => {
    setForm(f => ({
      ...f,
      selectedHolidayIds: f.selectedHolidayIds.includes(id)
        ? f.selectedHolidayIds.filter(x => x !== id)
        : [...f.selectedHolidayIds, id],
    }));
  };

  const renderStatusBadge = (status: string) => {
    const config = statusConfig[status] || statusConfig.Draft;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgClass}`}>
        <Icon className="w-3 h-3" />
        {t(status)}
      </span>
    );
  };

  const renderOptionRow = (list: RestrictedHolidayListView, option: RestrictedHolidayOptionView) => {
    const isSelected = list.mySelectedOptionIds.includes(option.id);
    const canSelect = list.isSelectionOpen && list.mySelectionCount < list.maxSelections;

    return (
      <div
        key={option.id}
        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
          isSelected ? 'border-primary/30 bg-primary/10' : 'border-border bg-card'
        }`}
        data-testid={`option-${option.id}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
          }`}>
            {isSelected ? <Check className="w-4 h-4" /> : <Calendar className="w-4 h-4" />}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{option.holidayName}</p>
            <p className="text-xs text-muted-foreground">{formatDate(option.date)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isHr && (
            <span className="text-xs text-muted-foreground">{option.totalSelections} {t('selected')}</span>
          )}
          {list.isSelectionOpen && (
            isSelected ? (
              <button
                onClick={() => handleDeselect(list.id, option.id)}
                disabled={actionLoading === `deselect-${option.id}`}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                data-testid={`deselect-${option.id}`}
              >
                {actionLoading === `deselect-${option.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                {t('Deselect')}
              </button>
            ) : canSelect ? (
              <button
                onClick={() => handleSelect(list.id, option.id)}
                disabled={actionLoading === `select-${option.id}`}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/10 disabled:opacity-50 transition-colors"
                data-testid={`select-${option.id}`}
              >
                {actionLoading === `select-${option.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {t('Select')}
              </button>
            ) : null
          )}
        </div>
      </div>
    );
  };

  const content = (
    <>
      {/* Header — only shown in standalone mode */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="restricted-holidays-title">{t('Restricted Holidays')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('restrictedHolidaysDescription')}</p>
          </div>
          {isHr && (
            <button
              onClick={() => {
                loadHolidays();
                setCreateModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
              data-testid="create-restricted-list-btn"
            >
              <Plus className="w-4 h-4" />
              {t('Create List')}
            </button>
          )}
        </div>
      )}

      {/* Embedded header — compact with create button */}
      {embedded && isHr && (
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={() => {
              loadHolidays();
              setCreateModal(true);
            }}
            className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
            data-testid="create-restricted-list-btn"
          >
            <Plus className="w-4 h-4" />
            {t('Create List')}
          </button>
        </div>
      )}

      {/* Lists */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : lists.length === 0 ? (
        <div className="text-center py-20" data-testid="restricted-empty">
          <CalendarCheck className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">{t('noRestrictedLists')}</p>
          <p className="text-sm text-muted-foreground mt-1">{t('noRestrictedListsDescription')}</p>
        </div>
      ) : (
        <div className="grid gap-6">
          {lists.map(list => (
            <div key={list.id} className="bg-card rounded-xl border border-border overflow-hidden" data-testid={`restricted-list-${list.id}`}>
              <div className="p-5 border-b border-border">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <CalendarCheck className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{list.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('Year')}: {list.year} · {t('Select up to')} {list.maxSelections} {t('of')} {list.options.length}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {renderStatusBadge(list.status)}
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{t('Selection Period')}: {formatDate(list.selectionStartDate)} - {formatDate(list.selectionEndDate)}</span>
                  {list.isSelectionOpen && (
                    <span className="text-green-600 font-medium">{t('selectionOpenNow')}</span>
                  )}
                </div>

                {/* Selection progress */}
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 bg-muted rounded-full h-2">
                    <div
                      className="bg-primary rounded-full h-2 transition-all"
                      style={{ width: `${(list.mySelectionCount / list.maxSelections) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">
                    {list.mySelectionCount}/{list.maxSelections} {t('selected')}
                  </span>
                </div>

                {/* HR actions */}
                {isHr && (
                  <div className="mt-3 flex gap-2">
                    {list.status === 'Draft' && (
                      <button
                        onClick={() => handleOpenList(list.id)}
                        disabled={actionLoading === `open-${list.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
                        data-testid={`open-list-${list.id}`}
                      >
                        {actionLoading === `open-${list.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />}
                        {t('Open Selections')}
                      </button>
                    )}
                    {list.status === 'Open' && (
                      <button
                        onClick={() => handleCloseList(list.id)}
                        disabled={actionLoading === `close-${list.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground bg-muted rounded-lg hover:bg-muted disabled:opacity-50 transition-colors"
                        data-testid={`close-list-${list.id}`}
                      >
                        {actionLoading === `close-${list.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />}
                        {t('Close Selections')}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Options list */}
              <div className="p-5 space-y-2">
                {list.options.map(option => renderOptionRow(list, option))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent size="lg" data-testid="create-restricted-modal">
          <DialogHeader>
            <DialogTitle>{t('Create Restricted Holiday List')}</DialogTitle>
            <DialogDescription>{t('restrictedCreateDescription')}</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('List Name')}</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                placeholder={t('restrictedListNamePlaceholder')}
                data-testid="restricted-list-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('Year')}</label>
                <input
                  type="number"
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) || new Date().getFullYear() })}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="restricted-year"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('Max Selections')}</label>
                <input
                  type="number"
                  min={1}
                  value={form.maxSelections}
                  onChange={(e) => setForm({ ...form, maxSelections: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="restricted-max-selections"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('Selection Start')}</label>
                <DateField
                  value={form.selectionStartDate}
                  onChange={(v) => setForm({ ...form, selectionStartDate: v })}
                  data-testid="restricted-start-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('Selection End')}</label>
                <DateField
                  value={form.selectionEndDate}
                  onChange={(v) => setForm({ ...form, selectionEndDate: v })}
                  data-testid="restricted-end-date"
                />
              </div>
            </div>

            {/* Holiday picker */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('Select Holidays')} ({form.selectedHolidayIds.length} {t('selected')})
              </label>
              {holidays.length === 0 ? (
                <div className="text-center py-4 text-sm text-muted-foreground">{t('noHolidaysAvailable')}</div>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                  {holidays.map(h => (
                    <label
                      key={h.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-muted cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.selectedHolidayIds.includes(h.id)}
                        onChange={() => toggleHolidayId(h.id)}
                        className="rounded border-border text-primary focus-visible:ring-ring"
                        data-testid={`holiday-checkbox-${h.id}`}
                      />
                      <div className="flex-1">
                        <span className="text-sm text-foreground">{h.name}</span>
                        <span className="text-xs text-muted-foreground ms-2">{formatDate(String(h.date))}</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => setCreateModal(false)} variant="outline" data-testid="cancel-create-restricted-btn">
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createLoading || !form.name || !form.selectionStartDate || !form.selectionEndDate || form.selectedHolidayIds.length === 0}
              loading={createLoading}
              data-testid="submit-restricted-list-btn"
            >
              {t('Create List')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 end-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-success text-success-foreground' : 'bg-destructive text-destructive-foreground'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {toast.message}
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div data-testid="restricted-holidays-embedded">{content}</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {content}
    </div>
  );
};

const mapStateToProps = (state: { user: { userId: string; company: { id: number }; role: string } }) => ({
  userId: state.user.userId,
  company: state.user.company,
  role: state.user.role,
});

export default connect(mapStateToProps)(RestrictedHolidays);
